import React, { useState, useEffect, Fragment, useRef } from 'react';
import TeamSearchBar from '../../SearchBars/TeamSearchBar/TeamSearchBar';
import CompareTeamGeneralInfo from '../TeamComparisonModal/CompareTeamGeneralInfo/CompareTeamGeneralInfo';
import CompareTeamEnvironmentScores from '../TeamComparisonModal/CompareTeamEnvironmentScores/CompareTeamEnvironmentScores';
import { playerAvatarImgClassName } from '../../../utils/avatarClassNames';

const TeamComparisonModal = ({
	get_teams_endpoint,
	isTeamCompareModalOpen,
	handleTeamComparModalClose,
	selectedTeams,
	handleTeamSelect,
	handleTeamRemove
}) => {
	const [
		showComparison,
		setShowComparison
	] = useState(false);
	const modalRef = useRef(null);

	const handleClickOutsideTeamComparisonModal = (event) => {
		if (!modalRef.current.contains(event.target)) {
			isTeamCompareModalOpen && handleTeamComparModalClose();
		}
	};

	// Prevent background scrolling when the modal is open
	useEffect(
		() => {
			if (isTeamCompareModalOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[
			isTeamCompareModalOpen
		]
	);

	useEffect(
		() => {
			if (isTeamCompareModalOpen) {
				document.addEventListener(
					'mousedown',
					handleClickOutsideTeamComparisonModal
				);
			} else {
				document.removeEventListener(
					'mousedown',
					handleClickOutsideTeamComparisonModal
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					handleClickOutsideTeamComparisonModal
				);
			};
		},
		[
			isTeamCompareModalOpen
		]
	);

	useEffect(
		() => {
			if (!isTeamCompareModalOpen) {
				setShowComparison(false);
			}
		},
		[
			isTeamCompareModalOpen
		]
	);

	const handleCompareClick = () => {
		setShowComparison(true);
	};

	const comparisonTable = (
		<div className="mt-4 overflow-y-auto border border-gray-200 dark:border-dark-border rounded-md">
			<div className="overflow-x-auto max-h-screen">
				<table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
					<thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 bg-gray-100 z-50">
						<tr>
							<th
								scope="col"
								className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-800"
							>
								Category
							</th>
							{selectedTeams.map((team, index) => (
								<th
									key={`team-${index}`}
									scope="col"
									className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-800"
								>
									<div className="flex items-center">
										<div className="relative mr-4">
											<img
												className={playerAvatarImgClassName}
												src={`${team.image_url}?w=75&h=75`}
												alt={`${team.name}`}
												style={{
													width: '75px',
													height: '75px',
													minWidth: '75px',
													minHeight: '75px'
												}}
											/>
										</div>
										<div>{team.name}</div>
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-dark-border">
						<CompareTeamGeneralInfo selectedTeams={selectedTeams} />
						<tr>
							<td colSpan={selectedTeams.length + 1}>
								<div className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-800">
									Team Environment Scores
								</div>
							</td>
						</tr>
						<CompareTeamEnvironmentScores selectedTeams={selectedTeams} />
					</tbody>
				</table>
			</div>
		</div>
	);

	return (
		<div
			className={`fixed inset-0 top-0 left-0 w-full h-full bg-black bg-opacity-50 ${isTeamCompareModalOpen
				? 'block'
				: 'hidden'}`}
			style={{
				zIndex: 1000,
				overflowY: 'auto'
			}}
		>
			<div
				className="p-4 relative mx-auto bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-auto max-w-full md:max-w-screen-2xl text-gray-800 dark:text-dark-text"
				style={{ zIndex: 1001 }}
				ref={modalRef}
			>
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center">
						<h2 className="text-2xl font-bold mr-4 text-gray-800 dark:text-dark-text">Compare Teams</h2>
						{showComparison && (
							<button
								className="pt-0.5 text-sm text-blue-500 rounded hover:text-blue-600 hover:underline"
								onClick={() => {
									setShowComparison(false);
								}}
							>
								Edit
							</button>
						)}
					</div>
					<button
						className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition duration-300"
						onClick={handleTeamComparModalClose}
					>
						<svg
							className="h-6 w-6 text-gray-600 dark:text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
				{!showComparison ? (
					<div>
						{selectedTeams.length < 5 && (
							<TeamSearchBar
								className="border border-gray-300 rounded-lg p-2 w-full test-sm text-gray-800"
								get_teams_endpoint={get_teams_endpoint}
								handleTeamSelect={handleTeamSelect}
								isVisibleByDefault={true}
								isCompareModal={true}
								isDarkMode={true}
							/>
						)}
						<div className="mt-4 flex flex-col">
							<h3 className="text-lg font-bold mb-2 text-gray-800 dark:text-dark-text">
								Selected Teams to Compare
							</h3>
							<div>
								{selectedTeams.map((team, index) => (
									<div
										key={team.id + '-team'}
										className="flex flex-col lg:flex-row justify-between items-center border-gray-200 dark:border-dark-border border py-4 rounded-xl my-6 shadow-sm bg-white dark:bg-dark-bg"
									>
										<div
											className="flex items-center w-full ml-10 flex-grow"
											style={{ minWidth: '240px' }}
										>
											<div className="flex items-center">
												<div className="relative mr-4">
													<img
														className={playerAvatarImgClassName}
														src={`${team.image_url}?w=75&h=75`}
														alt={`${team.name}`}
														style={{
															width: '75px',
															height: '75px',
															minWidth: '75px',
															minHeight: '75px'
														}}
													/>
												</div>
												<div>
													<div className="text-2xl text-gray-700 dark:text-dark-text">
														{team.name}
													</div>
												</div>
											</div>
										</div>
										<div className="lg:mr-10">
											{index !== 0 && (
												<button
													className="text-sm text-gray-500 dark:text-gray-300 hover:text-gray-800 dark:hover:text-dark-text"
													onClick={() => handleTeamRemove(team.id)}
												>
													Remove
												</button>
											)}
										</div>
									</div>
								))}
							</div>
							<div className="mt-2 self-end">
								<button
									className={`px-4 py-2 rounded-lg ${selectedTeams.length < 2 ||
									selectedTeams.length > 5
										? 'bg-gray-300 text-gray-500 cursor-not-allowed'
										: 'bg-blue-500 hover:bg-blue-600 text-white'}`}
									onClick={handleCompareClick}
									disabled={
										selectedTeams.length < 2 || selectedTeams.length > 5
									}
								>
									Compare
								</button>
							</div>
						</div>
					</div>
				) : (
					comparisonTable
				)}
			</div>
		</div>
	);
};

export default TeamComparisonModal;
